import { useEffect, useState } from 'react'
import type { NovaObraPendenciaInput, ObraPendenciaEnriquecida } from '@hooks/useObraPendencias.js'
import type { ObraPendenciaPrioridade } from '@services/db.js'
import { Modal } from '@components/primitives/Modal.js'
import { Button } from '@components/primitives/Button.js'
import { Input } from '@components/primitives/Input.js'
import { FASES_CONSTRUTIVAS } from '@utils/constants.js'

interface Props {
  open: boolean
  initial?: ObraPendenciaEnriquecida | null
  onClose: () => void
  onSalvar: (input: NovaObraPendenciaInput) => Promise<void>
  onEditar?: (id: number, input: NovaObraPendenciaInput) => Promise<void>
}

const EMPTY = {
  titulo: '',
  descricao: '',
  prioridade: 'media' as ObraPendenciaPrioridade,
  faseConstrutiva: '',
  responsavel: '',
  prazo: '',
}

export function PendenciaFormModal({ open, initial, onClose, onSalvar, onEditar }: Props) {
  const [form, setForm] = useState(EMPTY)
  const [salvando, setSalvando] = useState(false)

  useEffect(() => {
    if (!open) return
    if (initial) {
      setForm({
        titulo: initial.titulo,
        descricao: initial.descricao ?? '',
        prioridade: initial.prioridade,
        faseConstrutiva: initial.faseConstrutiva ?? '',
        responsavel: initial.responsavel ?? '',
        prazo: initial.prazo?.slice(0, 10) ?? '',
      })
    } else {
      setForm(EMPTY)
    }
  }, [open, initial])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!form.titulo.trim()) return
    setSalvando(true)
    const input: NovaObraPendenciaInput = {
      titulo: form.titulo.trim(),
      prioridade: form.prioridade,
      ...(form.descricao.trim() ? { descricao: form.descricao.trim() } : {}),
      ...(form.faseConstrutiva ? { faseConstrutiva: form.faseConstrutiva } : {}),
      ...(form.responsavel.trim() ? { responsavel: form.responsavel.trim() } : {}),
      ...(form.prazo ? { prazo: form.prazo } : {}),
    }
    if (initial && onEditar) await onEditar(initial.id, input)
    else await onSalvar(input)
    setSalvando(false)
    onClose()
  }

  return (
    <Modal
      open={open}
      title={initial ? 'Editar tarefa' : 'Nova tarefa'}
      onClose={onClose}
      actions={
        <>
          <Button variant='ghost' onClick={onClose} type='button'>
            Cancelar
          </Button>
          <Button
            variant='primary'
            type='submit'
            disabled={salvando || !form.titulo.trim()}
            onClick={handleSubmit}
          >
            {salvando ? 'Salvando…' : 'Salvar'}
          </Button>
        </>
      }
    >
      <form className='cf3-pend-form' onSubmit={handleSubmit}>
        <Input
          label='O que precisa resolver?'
          value={form.titulo}
          onChange={(e) => setForm((f) => ({ ...f, titulo: e.target.value }))}
          placeholder='Ex.: Pedreiro volta terça para acabamento'
          required
        />
        <label className='cf-label'>
          Detalhes
          <textarea
            className='cf-input cf3-pend-form__textarea'
            rows={3}
            value={form.descricao}
            onChange={(e) => setForm((f) => ({ ...f, descricao: e.target.value }))}
            placeholder='Local, combinado, observações…'
          />
        </label>
        <label className='cf-label'>
          Prioridade
          <select
            className='cf-input'
            value={form.prioridade}
            onChange={(e) =>
              setForm((f) => ({ ...f, prioridade: e.target.value as ObraPendenciaPrioridade }))
            }
          >
            <option value='baixa'>Baixa</option>
            <option value='media'>Média</option>
            <option value='alta'>Alta</option>
          </select>
        </label>
        <label className='cf-label'>
          Fase construtiva
          <select
            className='cf-input'
            value={form.faseConstrutiva}
            onChange={(e) => setForm((f) => ({ ...f, faseConstrutiva: e.target.value }))}
          >
            <option value=''>Não definida</option>
            {FASES_CONSTRUTIVAS.map((f) => (
              <option key={f.id} value={f.id}>
                {f.label}
              </option>
            ))}
          </select>
        </label>
        <Input
          label='Responsável'
          value={form.responsavel}
          onChange={(e) => setForm((f) => ({ ...f, responsavel: e.target.value }))}
          placeholder='Pedreiro, empreiteiro, você…'
        />
        <Input
          label='Prazo'
          type='date'
          value={form.prazo}
          onChange={(e) => setForm((f) => ({ ...f, prazo: e.target.value }))}
        />
      </form>
    </Modal>
  )
}
